import React from "react";
import { Link } from "react-router-dom";
import { Checkbox, Grid, Icon, Popup } from "semantic-ui-react";
import TextField from "../../../../components/FormComponents/TextField";
import styles from "./PriceInputs.module.scss";

type Props = {
	onChange: (e: React.FormEvent) => void;
	onDiscountToggle: (checked: boolean) => void;
	price: number | string;
	discount?: number | string;
	isDiscountEnabled: boolean;
	discountError?: string;
	omnibusReferenceLabel?: string;
};

const PriceInputs: React.FC<Props> = ({
	onChange,
	onDiscountToggle,
	price,
	discount = 0,
	isDiscountEnabled,
	discountError = "",
	omnibusReferenceLabel = "",
}) => {
	const parseNumber = (value: number | string): number =>
		typeof value === "number"
			? value
			: Number(String(value).replace(",", ".").trim());

	const numericPrice = parseNumber(price);
	const discountMax =
		Number.isFinite(numericPrice) && numericPrice > 0
			? Math.max(Number((numericPrice - 0.01).toFixed(2)), 0.01)
			: undefined;
	const omnibusPopupContent = (
		<div className={styles.omnibusPopupContent}>
			<p>
				Podczas dodawania promocji wyświetlamy najniższą cenę produktu z
				ostatnich 30 dni, ponieważ wymagają tego przepisy prawa.
			</p>
			<p>
				Przy wprowadzaniu obniżki system automatycznie pokazuje najniższą cenę
				tego produktu w ciągu 30 dni przed promocją, aby klienci mogli realnie
				ocenić wysokość rabatu.
			</p>
			<Link to="/omnibus-info" className={styles.omnibusPopupLink}>
				Jak działa Omnibus w aplikacji?
			</Link>
		</div>
	);

	return (
		<div className={styles.priceSection}>
			<h3 className={styles.sectionTitle}>Pricing Information</h3>
			<Grid columns={3} stackable className={styles.grid}>
				<Grid.Row>
					<Grid.Column>
						<div className={styles.controlRow}>
							<div
								className={styles.controlRowPlaceholder}
								aria-hidden="true"
							/>
						</div>
						<TextField
							placeholder="0.00"
							name="price"
							type="number"
							value={price}
							onChange={onChange}
							info="Base price for your pattern"
							min={0.01}
							step="0.01"
							inputMode="decimal"
							required
						/>
					</Grid.Column>
					<Grid.Column>
						<div
							className={`${styles.controlRow} ${
								!isDiscountEnabled ? styles.controlRowDiscountOff : ""
							}`}
						>
							<div
								className={`${styles.discountToggleWrap} ${
									!isDiscountEnabled ? styles.discountToggleWrapLower : ""
								}`}
							>
								<div className={styles.discountToggleRow}>
									<Popup
										on="hover"
										hoverable
										position="top center"
										trigger={
											<span className={styles.discountTogglePopupTarget}>
												<Checkbox
													checked={isDiscountEnabled}
													label="Włącz obniżkę (oznaczenie promocji)"
													onChange={(_, data) =>
														onDiscountToggle(Boolean(data.checked))
													}
												/>
											</span>
										}
										content={omnibusPopupContent}
									/>
									<Popup
										on="hover"
										hoverable
										position="top center"
										trigger={
											<button
												type="button"
												className={styles.omnibusInfoTrigger}
												aria-label="Informacje o zasadach Omnibus"
											>
												<Icon name="info circle" />
											</button>
										}
										content={omnibusPopupContent}
									/>
								</div>
							</div>
						</div>

						{isDiscountEnabled && (
							<>
								<TextField
									placeholder="0.00"
									name="discount"
									value={discount}
									type="number"
									onChange={onChange}
									error={discountError}
									info="Cena po obniżce"
									min={0.01}
									max={discountMax}
									step="0.01"
									inputMode="decimal"
									required={isDiscountEnabled}
								/>

								<div className={styles.omnibusHint}>
									{omnibusReferenceLabel
										? `Najniższa cena z ostatnich 30 dni: ${omnibusReferenceLabel}.`
										: "Najniższa cena z ostatnich 30 dni: brak danych."}
								</div>
							</>
						)}
					</Grid.Column>
					<Grid.Column>
						<div className={styles.controlRow}>
							<div
								className={styles.controlRowPlaceholder}
								aria-hidden="true"
							/>
						</div>
						<label className={styles.label}>Currency</label>
						<div className={styles.currencyDropdown}>EUR (€)</div>
						<div className={styles.helperText}>
							Waluta płatności jest stała: EUR
						</div>
					</Grid.Column>
				</Grid.Row>
			</Grid>
		</div>
	);
};

export default PriceInputs;
